import * as React from 'react';
import { find, pull, uniq } from 'lodash';

import { iCategoryType } from '@/interfaces/categories';
import { useForm } from '@/hooks';

import { Select } from '@/components/Select';
import { Button } from '@/components/content';
import { ValidationErrorsList } from '@/components/validation/ValidationErrorsList';

interface iSelectOption {
    label: string;
    value: string;
}

export interface iProps {
    /**
     * Available category types.
     */
    categorizables: iSelectOption[];

    /**
     * The category type to modify.
     * Defaults will be used if not provided.
     */
    categoryType?: iCategoryType;

    /**
     * Puts the form into a busy state.
     */
    isBusy?: boolean;

    /**
     * Function called when the user saves the form.
     */
    onSave?: (categoryType: iCategoryType) => any;
}

const defaultCategoryType = {
    categorizables: [],
    name: '',
} as iCategoryType;

export const CategoryTypeForm: React.SFC<iProps> = (props: iProps) => {

    /**
     * Define hooks.
     */
    const formData = useForm({
        initialValues: props.categoryType || defaultCategoryType,
        rules: {
            name: ['required'],
            categorizables: ['required', 'array_min:1'],
        },
        validationFieldNames: {
            categorizables: 'site features',
        },
    });
    const [validationErrors, setValidationErrors] = React.useState<null | Record<string, any>>(null);

    /**
     * Finds label for a categorizable value.
     *
     * @param {string} categorizableValue
     * @returns {void | string}
     */
    const getCategorizableLabel = (categorizableValue: string): null | string => {
        const option = find(props.categorizables, { value: categorizableValue });
        if (!option) { return null; }
        return option.label;
    };

    /**
     * Calls onSave prop if the form passes validation.
     *
     * @param {React.MouseEvent<HTMLButtonElement>} event
     * @returns {void}
     */
    const handleSave = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();

        const errors = formData.validationErrors.all();
        setValidationErrors(errors);

        if (!Object.keys(errors).length) {
            props.onSave(formData.data);
        }
    };

    /**
     * Adds a selected categorizable to the category type.
     *
     * @param categorizable
     * @returns {void}
     */
    const selectCategorizable = (categorizable: iSelectOption): void => {
        const categorizables = [...formData.data.categorizables, categorizable.value];
        formData.setField('categorizables')(uniq(categorizables));
    };

    /**
     * Removes a selected categorizable from the category type.
     *
     * @param {string} categorizableValue
     * @returns {void}
     */
    const removeCategorizable = (categorizableValue: string): void => {
        const categorizables = pull([...formData.data.categorizables], categorizableValue);
        formData.setField('categorizables')(uniq(categorizables));
    };

    /**
     * Renders an item for the list of selected categorizables.
     *
     * @param {string} categorizableValue
     * @returns {React.ReactElement}
     */
    const renderCategorizableListItem = (categorizableValue: string): React.ReactElement => (
        <div className="list__item list-item" key={categorizableValue}>
            <div className="list-item__controls">
                <button
                    className="list-item__control button button--icon"
                    onClick={removeCategorizable.bind(this, categorizableValue)}
                ><i className="fa fa-trash button__icon"></i></button>
            </div>
            {getCategorizableLabel(categorizableValue)}
        </div>
    );

    return (
        <form className="form category-type-form">
            <div className="input-group">
                <label htmlFor="" className="input-group__label">Name</label>
                <input
                    autoFocus
                    disabled={props.isBusy}
                    onChange={formData.setField('name')}
                    placeholder="Name this category group"
                    type="text"
                    value={formData.data.name}
                />
            </div>

            <div className="input-group">
                <label htmlFor="" className="input-group__label">Site features</label>
                <Select
                    disabled={props.isBusy}
                    items={props.categorizables.filter(item => !formData.data.categorizables.includes(item.value))}
                    onSelect={selectCategorizable}
                    placeholder="Choose a feature"
                />
            </div>

            <ul className="list list--alternate">
                {formData.data.categorizables.map(renderCategorizableListItem)}
            </ul>

            <Button
                className="form__submit"
                disabled={props.isBusy}
                fill
                loading={props.isBusy}
                onClick={handleSave}
                size="large"
            >Save</Button>

            <ValidationErrorsList className="form__validation-results" errors={validationErrors} />
        </form>
    );
};
CategoryTypeForm.displayName = 'CategoryTypeForm';
